(function () {
  'use strict';

  var ROOT_SELECTOR = [
    '[data-sh-lighting-editorial]',
    '[data-sh-trending-editorial]',
    '[data-sh-home-accessories]',
    '[data-sh-textile-architecture]',
    '[data-sh-furniture-room-anchors]'
  ].join(',');

  var rootStates = new WeakMap();

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  function initRoot(root) {
    if (!root || rootStates.has(root)) return;

    var config = getConfig(root);
    if (!config) return;

    var style = root.dataset.variantSelectorStyle === 'quiet-count' ? 'quiet-count' : 'adaptive';
    var state = {
      root: root,
      config: config,
      style: style,
      scheduled: false,
      activeCard: null,
      lastFocus: null,
      drawer: null,
      backdrop: null,
      observer: null,
      clickHandler: null,
      keyHandler: null
    };

    rootStates.set(root, state);
    root.dataset.shVariantSelectorReady = 'true';
    createDrawer(state);

    state.clickHandler = function (event) { onRootClick(state, event); };
    state.keyHandler = function (event) { onKeydown(state, event); };
    root.addEventListener('click', state.clickHandler);
    document.addEventListener('keydown', state.keyHandler);

    state.observer = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (mutation) {
        var target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
        if (!target) return false;
        if (target.closest('.sh-cv__drawer, .sh-cv__backdrop, .sh-cv__control')) return false;
        return !!target.closest(state.config.cardSelector);
      });
      if (relevant) scheduleEnhance(state);
    });
    state.observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'href', 'src', 'data-handle']
    });

    scheduleEnhance(state);
  }

  function getConfig(root) {
    if (root.matches('[data-sh-furniture-room-anchors]')) {
      return {
        cardSelector: '[data-sh-furniture-card]',
        sourceSelector: '.sh-furn__variants',
        nativeChoiceSelector: '[data-variant-id]',
        productLinkSelector: '[data-product-link]',
        quickButtonSelector: '[data-quick-add]',
        imageSelector: '.sh-furn__image--primary',
        titleSelector: '.sh-furn__title-price h3 a'
      };
    }

    return {
      cardSelector: '[data-sh-card]',
      sourceSelector: '[data-sh-card-swatches]',
      nativeChoiceSelector: '[data-sh-member], [data-sh-product-swatch]',
      productLinkSelector: '[data-sh-product-link]',
      quickButtonSelector: '[data-sh-quick]',
      imageSelector: '[data-sh-primary-image]',
      titleSelector: '[data-sh-card-title]'
    };
  }

  function scheduleEnhance(state) {
    if (state.scheduled) return;
    state.scheduled = true;
    window.requestAnimationFrame(function () {
      state.scheduled = false;
      enhanceAll(state);
    });
  }

  function enhanceAll(state) {
    state.root.querySelectorAll(state.config.cardSelector).forEach(function (card) {
      enhanceCard(state, card);
    });
    if (state.activeCard && state.drawer.classList.contains('is-open')) renderDrawer(state);
  }

  function enhanceCard(state, card) {
    var source = card.querySelector(state.config.sourceSelector);
    if (!source) return;

    var sourceWrap = directChildByClass(source, 'sh-cv__source');
    var control = directChildByClass(source, 'sh-cv__control');

    if (!sourceWrap) {
      var originalButtons = Array.prototype.slice.call(source.querySelectorAll(state.config.nativeChoiceSelector));
      if (originalButtons.length < 2) {
        source.hidden = true;
        source.dataset.shCvEmpty = 'true';
        return;
      }

      source.hidden = false;
      source.removeAttribute('data-sh-cv-empty');
      sourceWrap = document.createElement('div');
      sourceWrap.className = 'sh-cv__source';
      sourceWrap.hidden = true;
      originalButtons.forEach(function (button) { sourceWrap.appendChild(button); });
      source.innerHTML = '';
      source.appendChild(sourceWrap);
    }

    var choices = readChoices(state, card);
    if (choices.length < 2) {
      source.hidden = true;
      return;
    }

    source.hidden = false;
    var signature = state.style + '::' + choices.map(function (choice) {
      return choice.key + ':' + (choice.unavailable ? '0' : '1');
    }).join('|');

    if (!control || control.dataset.shCvSignature !== signature) {
      if (control) control.remove();
      control = buildControl(state, choices);
      control.dataset.shCvSignature = signature;
      source.appendChild(control);
    }

    syncControl(state, card, choices);
  }

  function directChildByClass(parent, className) {
    for (var i = 0; i < parent.children.length; i += 1) {
      if (parent.children[i].classList.contains(className)) return parent.children[i];
    }
    return null;
  }

  function readChoices(state, card) {
    var source = card.querySelector(state.config.sourceSelector);
    if (!source) return [];
    var sourceWrap = directChildByClass(source, 'sh-cv__source');
    if (!sourceWrap) return [];

    var rawChoices = Array.prototype.slice.call(sourceWrap.querySelectorAll(state.config.nativeChoiceSelector)).map(function (button, index) {
      var rawLabel = choiceLabel(button);
      var label = displayChoiceLabel(rawLabel);
      var colour = choiceColour(button);
      return {
        sourceIndex: index,
        key: normaliseChoiceKey(label || rawLabel || String(index)),
        label: label || rawLabel || ('Option ' + (index + 1)),
        colour: colour,
        isColour: !!colour,
        active: button.classList.contains('is-active'),
        unavailable: button.disabled || button.getAttribute('aria-disabled') === 'true' || button.dataset.available === 'false' || button.classList.contains('is-unavailable'),
        button: button,
        buttons: [button]
      };
    });

    /* Products with colour + size combinations can expose one native control per
       complete variant. Collapse those into one collection-card choice per finish. */
    var grouped = [];
    var byKey = Object.create(null);
    rawChoices.forEach(function (choice) {
      var key = choice.key || String(choice.sourceIndex);
      var existing = byKey[key];
      if (!existing) {
        choice.index = grouped.length;
        grouped.push(choice);
        byKey[key] = choice;
        return;
      }

      existing.buttons.push(choice.button);
      existing.active = existing.active || choice.active;
      existing.unavailable = existing.unavailable && choice.unavailable;
      if ((!existing.colour || existing.unavailable) && choice.colour && !choice.unavailable) {
        existing.colour = choice.colour;
        existing.isColour = true;
        existing.button = choice.button;
      } else if (existing.button.disabled && !choice.button.disabled) {
        existing.button = choice.button;
      }
    });

    return grouped;
  }

  function choiceLabel(button) {
    var value = button.getAttribute('title') || button.dataset.optionValue || button.getAttribute('aria-label') || button.textContent || '';
    return String(value).replace(/^choose\s+/i, '').replace(/^select\s+/i, '').trim();
  }

  function displayChoiceLabel(value) {
    var label = String(value || '').trim();
    if (!label) return '';

    /* Keep the visual finish whether technical information appears before or
       after it: "200 × 200cm / Monochrome" and "Green / 30 × 12cm". */
    var parts = label.split(/\s*(?:—|–|\/|\|)\s*/).map(function (part) {
      return part.trim();
    }).filter(Boolean);

    if (parts.length > 1) {
      var technicalParts = parts.filter(isTechnicalChoicePart);
      var visualParts = parts.filter(function (part) { return !isTechnicalChoicePart(part); });
      if (technicalParts.length && visualParts.length) label = visualParts.join(' / ');
    }

    return label;
  }

  function isTechnicalChoicePart(value) {
    var part = String(value || '').trim().toLowerCase();
    if (!part) return false;
    return /^\d+(?:[.,]\d+)?\s*(?:×|x)\s*\d+/i.test(part) ||
      /\d+(?:[.,]\d+)?\s*(?:cm|mm|m|metres?|meters?|inches?|inch|in|ft|feet)\b/i.test(part) ||
      /\b(?:cm|mm|metres?|meters?|inches?|inch|ft|feet)\b/i.test(part) ||
      /\b(?:uk|eu|us|au)\s*plug\b/i.test(part) ||
      /\b(?:plug|usb(?:-c)?|socket|bulb|e14|e27|g9|gu10|watt|watts|volt|volts|mah)\b/i.test(part) ||
      /^\d+\s*-?\s*tiers?$/i.test(part) ||
      /^\d+\s*(?:pcs?|pieces?|magnets?|shelves?|packs?|sets?)$/i.test(part) ||
      /^\d+\s*x\s*(?:e14|e27|g9|gu10)\b/i.test(part) ||
      /^(?:size\s*)?(?:xs|s|m|l|xl|xxl|small|medium|large|single|double|king|super king|short|tall)$/i.test(part);
  }

  function normaliseChoiceKey(value) {
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function choiceColour(button) {
    if (button.dataset.shSwatchKind === 'text') return '';
    var value = button.style.getPropertyValue('--sh-swatch') || button.style.backgroundColor || button.style.background || '';
    var dot = button.querySelector('.sh-furn__variant-dot');
    if (dot) value = dot.style.getPropertyValue('--sh-dot') || dot.style.backgroundColor || value;
    value = String(value || '').trim();
    if (!value || value === 'transparent' || value === 'rgba(0, 0, 0, 0)') return '';
    return value;
  }

  function buildControl(state, choices) {
    var control = document.createElement('div');
    control.className = 'sh-cv__control';

    /* Curated Three is reserved for choices that can all be represented
       honestly as visual swatches. Named finishes, fabrics, patterns and
       other non-swatch choices automatically use the quieter active-label
       treatment instead of cramped abbreviated pills. */
    var useQuietControl = state.style === 'quiet-count' || !choices.every(function (choice) {
      return choice.isColour;
    });
    control.dataset.shCvControl = useQuietControl ? 'quiet-count' : 'adaptive';

    if (useQuietControl) {
      control.innerHTML = [
        '<button class="sh-cv__quiet" type="button" data-sh-cv-open-drawer aria-haspopup="dialog">',
          '<span class="sh-cv__quiet-dot" aria-hidden="true"></span>',
          '<span class="sh-cv__quiet-label"></span>',
          '<span class="sh-cv__quiet-count"></span>',
        '</button>'
      ].join('');
      return control;
    }

    var visibleCount = Math.min(3, choices.length);
    var row = document.createElement('div');
    row.className = 'sh-cv__row';

    for (var i = 0; i < visibleCount; i += 1) {
      row.appendChild(createChoiceButton(choices[i]));
    }

    if (choices.length === 4) {
      var fourth = document.createElement('button');
      fourth.className = 'sh-cv__more';
      fourth.type = 'button';
      fourth.dataset.shCvChoiceIndex = '3';
      fourth.textContent = '+1';
      fourth.setAttribute('aria-label', 'Preview ' + choices[3].label);
      fourth.title = choices[3].label;
      row.appendChild(fourth);
    } else if (choices.length >= 5) {
      var hiddenCount = choices.length - 3;
      var more = document.createElement('button');
      more.className = 'sh-cv__more';
      more.type = 'button';
      more.dataset.shCvOpenDrawer = '';
      more.setAttribute('aria-haspopup', 'dialog');
      more.setAttribute('aria-label', 'View all ' + choices.length + (choices.every(function (choice) { return choice.isColour; }) ? ' colours' : ' finishes'));
      more.title = 'View all ' + choices.length + (choices.every(function (choice) { return choice.isColour; }) ? ' colours' : ' finishes');
      more.textContent = '+' + hiddenCount;
      row.appendChild(more);
    }

    control.appendChild(row);
    return control;
  }

  function createChoiceButton(choice) {
    var button = document.createElement('button');
    button.type = 'button';
    button.dataset.shCvChoiceIndex = String(choice.index);
    button.setAttribute('aria-label', 'Preview ' + choice.label);
    button.title = choice.label;

    if (choice.isColour) {
      button.className = 'sh-cv__choice';
      button.style.setProperty('--sh-cv-colour', choice.colour);
    } else {
      button.className = 'sh-cv__choice sh-cv__choice--text';
      button.textContent = shortLabel(choice.label);
    }

    if (choice.unavailable) {
      button.disabled = true;
      button.classList.add('is-unavailable');
    }
    return button;
  }

  function shortLabel(label) {
    var text = String(label || '').trim();
    if (text.length <= 9) return text;
    var words = text.split(/\s*\/\s*|\s+-\s+|\s+/).filter(Boolean);
    return words.length > 1 ? words.map(function (word) { return word.charAt(0); }).join('').slice(0, 4).toUpperCase() : text.slice(0, 8) + '…';
  }

  function syncControl(state, card, choices) {
    choices = choices || readChoices(state, card);
    if (!choices.length) return;

    var source = card.querySelector(state.config.sourceSelector);
    if (!source) return;
    var control = directChildByClass(source, 'sh-cv__control');
    if (!control) return;

    var activeIndex = choices.findIndex(function (choice) { return choice.active; });
    if (activeIndex < 0) activeIndex = 0;
    var active = choices[activeIndex];

    control.querySelectorAll('[data-sh-cv-choice-index]').forEach(function (button) {
      var index = Number(button.dataset.shCvChoiceIndex);
      var choice = choices[index];
      if (!choice) return;
      button.classList.toggle('is-active', index === activeIndex);
      button.classList.toggle('is-unavailable', choice.unavailable);
      button.setAttribute('aria-pressed', index === activeIndex ? 'true' : 'false');
      if (button.classList.contains('sh-cv__more')) {
        button.style.setProperty('--sh-cv-active-colour', choice.colour || '#f2eee5');
      }
    });


    var more = control.querySelector('[data-sh-cv-open-drawer].sh-cv__more');
    if (more) {
      more.classList.toggle('is-active', activeIndex >= 3);
      more.style.setProperty('--sh-cv-active-colour', active.colour || '#f2eee5');
    }

    var quiet = control.querySelector('.sh-cv__quiet');
    if (quiet) {
      quiet.classList.toggle('is-text', !active.isColour);
      if (active.isColour) quiet.style.setProperty('--sh-cv-colour', active.colour);
      else quiet.style.removeProperty('--sh-cv-colour');
      var quietLabel = quiet.querySelector('.sh-cv__quiet-label');
      var quietCount = quiet.querySelector('.sh-cv__quiet-count');
      if (quietLabel) quietLabel.textContent = active.label;
      if (quietCount) quietCount.textContent = choices.length + (choices.every(function (choice) { return choice.isColour; }) ? ' colours' : ' finishes');
      quiet.setAttribute('aria-label', active.label + '. View all ' + choices.length + (choices.every(function (choice) { return choice.isColour; }) ? ' colours' : ' finishes'));
    }
  }

  function onRootClick(state, event) {
    var choiceButton = event.target.closest('[data-sh-cv-choice-index]');
    if (choiceButton && state.root.contains(choiceButton)) {
      event.preventDefault();
      event.stopPropagation();
      var choiceCard = choiceButton.closest(state.config.cardSelector);
      if (!choiceCard) return;
      chooseFromCard(state, choiceCard, Number(choiceButton.dataset.shCvChoiceIndex));
      return;
    }

    var openButton = event.target.closest('[data-sh-cv-open-drawer]');
    if (openButton && state.root.contains(openButton)) {
      event.preventDefault();
      event.stopPropagation();
      var openCard = openButton.closest(state.config.cardSelector);
      if (openCard) openDrawer(state, openCard, openButton);
      return;
    }

    var drawerChoice = event.target.closest('[data-sh-cv-drawer-choice]');
    if (drawerChoice && state.root.contains(drawerChoice)) {
      event.preventDefault();
      event.stopPropagation();
      if (state.activeCard) {
        chooseFromCard(state, state.activeCard, Number(drawerChoice.dataset.shCvDrawerChoice));
        window.setTimeout(function () { renderDrawer(state); }, 120);
      }
      return;
    }

    if (event.target.closest('[data-sh-cv-close-drawer]') || event.target === state.backdrop) {
      event.preventDefault();
      closeDrawer(state);
      return;
    }

  }

  function chooseFromCard(state, card, index) {
    enhanceCard(state, card);
    var choices = readChoices(state, card);
    var choice = choices[index];
    if (!choice || choice.unavailable || !choice.button) return;

    var source = card.querySelector(state.config.sourceSelector);
    var sourceWrap = source ? directChildByClass(source, 'sh-cv__source') : null;
    if (sourceWrap) {
      sourceWrap.querySelectorAll(state.config.nativeChoiceSelector).forEach(function (button) {
        button.classList.remove('is-active');
      });
    }
    choice.button.classList.add('is-active');
    syncControl(state, card, readChoices(state, card));
    choice.button.click();

    window.setTimeout(function () {
      enhanceCard(state, card);
      syncControl(state, card);
      if (state.activeCard === card && state.drawer.classList.contains('is-open')) renderDrawer(state);
    }, 80);
  }

  function createDrawer(state) {
    var backdrop = document.createElement('div');
    backdrop.className = 'sh-cv__backdrop';
    backdrop.dataset.shCvCloseDrawer = '';
    backdrop.setAttribute('aria-hidden', 'true');

    var drawer = document.createElement('aside');
    drawer.className = 'sh-cv__drawer';
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('aria-label', 'Choose a product option');
    drawer.innerHTML = [
      '<div class="sh-cv__drawer-head">',
        '<div><small>Colour &amp; finish</small><strong>Choose your version</strong></div>',
        '<button class="sh-cv__drawer-close" type="button" data-sh-cv-close-drawer aria-label="Close option drawer">×</button>',
      '</div>',
      '<div class="sh-cv__drawer-scroll">',
        '<div class="sh-cv__drawer-media"><img data-sh-cv-drawer-image alt=""></div>',
        '<div class="sh-cv__drawer-body">',
          '<p class="sh-cv__drawer-kicker">Available options</p>',
          '<h2 data-sh-cv-drawer-title></h2>',
          '<div class="sh-cv__drawer-selection"><span>Selected</span><strong data-sh-cv-drawer-selected></strong></div>',
          '<div class="sh-cv__drawer-options" data-sh-cv-drawer-options></div>',
        '</div>',
      '</div>',
      '<div class="sh-cv__drawer-foot">',
        '<a class="sh-cv__drawer-link" data-sh-cv-drawer-link href="#">View product</a>',
      '</div>'
    ].join('');

    state.root.appendChild(backdrop);
    state.root.appendChild(drawer);
    state.backdrop = backdrop;
    state.drawer = drawer;
  }

  function openDrawer(state, card, trigger) {
    state.activeCard = card;
    state.lastFocus = trigger || document.activeElement;
    renderDrawer(state);
    state.backdrop.classList.add('is-open');
    state.drawer.classList.add('is-open');
    state.backdrop.setAttribute('aria-hidden', 'false');
    state.drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sh-cv-lock');
    var close = state.drawer.querySelector('[data-sh-cv-close-drawer]');
    if (close) close.focus();
  }

  function closeDrawer(state) {
    if (!state.drawer || !state.drawer.classList.contains('is-open')) return;
    state.backdrop.classList.remove('is-open');
    state.drawer.classList.remove('is-open');
    state.backdrop.setAttribute('aria-hidden', 'true');
    state.drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('sh-cv-lock');
    var focus = state.lastFocus;
    state.activeCard = null;
    state.lastFocus = null;
    if (focus && typeof focus.focus === 'function') window.setTimeout(function () { focus.focus(); }, 30);
  }

  function renderDrawer(state) {
    var card = state.activeCard;
    if (!card || !document.documentElement.contains(card)) {
      closeDrawer(state);
      return;
    }

    enhanceCard(state, card);
    var choices = readChoices(state, card);
    if (!choices.length) {
      closeDrawer(state);
      return;
    }

    var activeIndex = choices.findIndex(function (choice) { return choice.active; });
    if (activeIndex < 0) activeIndex = 0;
    var active = choices[activeIndex];
    var titleNode = card.querySelector(state.config.titleSelector);
    var imageNode = card.querySelector(state.config.imageSelector);
    var productLink = card.querySelector(state.config.productLinkSelector);
    var quickButton = card.querySelector(state.config.quickButtonSelector);

    var drawerTitle = state.drawer.querySelector('[data-sh-cv-drawer-title]');
    var drawerImage = state.drawer.querySelector('[data-sh-cv-drawer-image]');
    var drawerSelected = state.drawer.querySelector('[data-sh-cv-drawer-selected]');
    var options = state.drawer.querySelector('[data-sh-cv-drawer-options]');
    var productCta = state.drawer.querySelector('[data-sh-cv-drawer-link]');

    if (drawerTitle) drawerTitle.textContent = titleNode ? titleNode.textContent.trim() : 'Choose your version';
    if (drawerImage && imageNode) {
      drawerImage.src = imageNode.currentSrc || imageNode.src || '';
      drawerImage.alt = imageNode.alt || (drawerTitle ? drawerTitle.textContent : '');
    }
    if (drawerSelected) drawerSelected.textContent = active.label;

    if (options) {
      options.innerHTML = choices.map(function (choice, index) {
        var visual = choice.isColour
          ? '<span class="sh-cv__drawer-choice-visual" style="--sh-cv-colour:' + escapeAttr(choice.colour) + '" aria-hidden="true"></span>'
          : '<span class="sh-cv__drawer-choice-visual is-text" aria-hidden="true">' + escapeHtml(shortLabel(choice.label)) + '</span>';
        return '<button class="sh-cv__drawer-choice' + (index === activeIndex ? ' is-active' : '') + (choice.unavailable ? ' is-unavailable' : '') + '" type="button" data-sh-cv-drawer-choice="' + index + '" aria-pressed="' + (index === activeIndex ? 'true' : 'false') + '"' + (choice.unavailable ? ' disabled' : '') + '>' + visual + '<span>' + escapeHtml(choice.label) + '</span></button>';
      }).join('');
    }

    if (productCta) {
      productCta.hidden = false;
      productCta.href = productLink ? productLink.href : '#';
    }
  }

  function onKeydown(state, event) {
    if (!state.drawer || !state.drawer.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer(state);
      return;
    }
    if (event.key !== 'Tab') return;

    var focusable = Array.prototype.slice.call(state.drawer.querySelectorAll('button:not([disabled]):not([hidden]), a[href]:not([hidden])'));
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }


  function destroyRoot(root) {
    var state = rootStates.get(root);
    if (!state) return;
    if (state.observer) state.observer.disconnect();
    if (state.clickHandler) root.removeEventListener('click', state.clickHandler);
    if (state.keyHandler) document.removeEventListener('keydown', state.keyHandler);
    if (state.drawer && state.drawer.parentNode) state.drawer.parentNode.removeChild(state.drawer);
    if (state.backdrop && state.backdrop.parentNode) state.backdrop.parentNode.removeChild(state.backdrop);
    if (state.activeCard) document.documentElement.classList.remove('sh-cv-lock');
    root.removeAttribute('data-sh-variant-selector-ready');
    rootStates.delete(root);
  }

  function destroyWithin(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) destroyRoot(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(ROOT_SELECTOR).forEach(destroyRoot);
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>'"]/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character];
    });
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/`/g, '&#96;');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroyWithin(event.target); });
}());
